// src/solana/roundsClient.ts
import {
  Connection,
  PublicKey,
  SystemProgram,
  Transaction,
  TransactionInstruction,
} from "@solana/web3.js";

import {
  derivePoolPdaV1,
  deriveVaultPdaV1,
  deriveMemberPdaV1,
  derivePoolPdaV2,
  deriveVaultPdaV2,
  deriveMemberPdaV2,
} from "./weareSusuPdas";

// ========= Types
export type PhantomProvider = {
  isPhantom?: boolean;
  publicKey?: any;
  connect: () => Promise<any>;
  signAndSendTransaction: (tx: Transaction) => Promise<{ signature: string }>;
};

export function getDefaultProgramId(): string {
  return "5bf9hRQ81Mnun8SDUfkanRL7hyCwBycBQLwhcZgkPJsd";
}

function u64ToLeBytes(x: bigint): Buffer {
  const buf = Buffer.alloc(8);
  let n = x;
  for (let i = 0; i < 8; i++) {
    buf[i] = Number(n & 0xffn);
    n >>= 8n;
  }
  return buf;
}

function ixData(discriminator: number[], argBytes?: Buffer): Buffer {
  const d = Buffer.from(discriminator);
  return argBytes ? Buffer.concat([d, argBytes]) : d;
}

function pubkeyToBytes(pk: PublicKey): Buffer {
  return Buffer.from(pk.toBytes());
}

function boolToU8(b: boolean): Buffer {
  return Buffer.from([b ? 1 : 0]);
}

async function sendPhantomTx(args: {
  connection: Connection;
  phantom: PhantomProvider;
  feePayer: PublicKey;
  ixs: TransactionInstruction[];
  onDebugLog?: (line: string) => void;
}): Promise<string> {
  const { connection, phantom, feePayer, ixs, onDebugLog } = args;

  const latest = await connection.getLatestBlockhash("confirmed");
  const tx = new Transaction({
    feePayer,
    recentBlockhash: latest.blockhash,
  }).add(...ixs);

  onDebugLog?.(`Simulating (RPC)...`);
  const sim = await connection.simulateTransaction(tx);
  if (sim.value.err) {
    onDebugLog?.(`Simulation error: ${JSON.stringify(sim.value.err)}`);
    throw new Error(`Simulation failed: ${JSON.stringify(sim.value.err)}`);
  }

  onDebugLog?.(`Sending...`);
  const res = await phantom.signAndSendTransaction(tx);
  const sig = res.signature;

  onDebugLog?.(`Sent: ${sig}`);
  onDebugLog?.(`Confirming...`);
  const conf = await connection.confirmTransaction(sig, "confirmed");
  if (conf.value.err) {
    throw new Error(`Transaction failed: ${JSON.stringify(conf.value.err)}`);
  }
  onDebugLog?.(`Confirmed.`);
  return sig;
}

// ========= Discriminators (from your weare_susu.json)
const DISC = {
  // V1
  create_pool: [233, 146, 209, 142, 207, 104, 64, 188],
  repair_vault: [100, 152, 67, 29, 93, 138, 69, 116],
  join_pool: [14, 65, 62, 16, 116, 17, 195, 107],
  contribute: [82, 33, 68, 131, 32, 0, 205, 95],

  // V2
  create_pool_v2: [133, 27, 21, 38, 67, 86, 91, 132],
  join_pool_v2: [91, 44, 221, 180, 210, 198, 213, 254],
  contribute_v2: [72, 65, 231, 106, 127, 64, 114, 110],
  execute_payout_v2: [74, 126, 34, 174, 140, 201, 99, 50],
  advance_round_v2: [230, 53, 75, 252, 181, 239, 109, 85],

  // Migration
  migrate_to_v2: [20, 136, 42, 223, 224, 213, 149, 32],
};

// ========= Helpers (best-practice idempotency checks)
async function getOwnerBase58(connection: Connection, pk: PublicKey): Promise<string | null> {
  try {
    const info = await connection.getAccountInfo(pk, "confirmed");
    return info?.owner?.toBase58?.() ?? null;
  } catch {
    return null;
  }
}

function isOwnedBy(infoOwnerBase58: string | null, programId: PublicKey): boolean {
  return !!infoOwnerBase58 && infoOwnerBase58 === programId.toBase58();
}

// ========= V1
export async function createPoolV1(args: {
  connection: Connection;
  authority: PublicKey;
  phantom: PhantomProvider;
  programId: PublicKey;
  onDebugLog?: (line: string) => void;
}): Promise<string> {
  const { connection, authority, phantom, programId, onDebugLog } = args;

  const [pool] = derivePoolPdaV1(programId, authority);
  const [vault] = deriveVaultPdaV1(programId, pool);

  onDebugLog?.(`CreatePool V1 Program: ${programId.toBase58()}`);
  onDebugLog?.(`Authority: ${authority.toBase58()}`);
  onDebugLog?.(`Pool PDA: ${pool.toBase58()}`);
  onDebugLog?.(`Vault PDA: ${vault.toBase58()}`);

  const ix = new TransactionInstruction({
    programId,
    keys: [
      { pubkey: authority, isSigner: true, isWritable: true },
      { pubkey: pool, isSigner: false, isWritable: true },
      { pubkey: vault, isSigner: false, isWritable: true },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
    ],
    data: ixData(DISC.create_pool),
  });

  return sendPhantomTx({
    connection,
    phantom,
    feePayer: authority,
    ixs: [ix],
    onDebugLog,
  });
}

export async function repairVaultV1(args: {
  connection: Connection;
  authority: PublicKey;
  phantom: PhantomProvider;
  programId: PublicKey;
  onDebugLog?: (line: string) => void;
}): Promise<string> {
  const { connection, authority, phantom, programId, onDebugLog } = args;

  const [pool] = derivePoolPdaV1(programId, authority);
  const [vault] = deriveVaultPdaV1(programId, pool);

  onDebugLog?.(`RepairVault V1 Program: ${programId.toBase58()}`);
  onDebugLog?.(`Authority: ${authority.toBase58()}`);
  onDebugLog?.(`Pool PDA: ${pool.toBase58()}`);
  onDebugLog?.(`Vault PDA: ${vault.toBase58()}`);

  const ix = new TransactionInstruction({
    programId,
    keys: [
      { pubkey: authority, isSigner: true, isWritable: true },
      { pubkey: pool, isSigner: false, isWritable: true },
      { pubkey: vault, isSigner: false, isWritable: true },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
    ],
    data: ixData(DISC.repair_vault),
  });

  return sendPhantomTx({
    connection,
    phantom,
    feePayer: authority,
    ixs: [ix],
    onDebugLog,
  });
}

export async function joinPoolV1(args: {
  connection: Connection;
  wallet: PublicKey;
  phantom: PhantomProvider;
  programId: PublicKey;
  onDebugLog?: (line: string) => void;
}): Promise<string> {
  const { connection, wallet, phantom, programId, onDebugLog } = args;

  const [pool] = derivePoolPdaV1(programId, wallet);
  const [member] = deriveMemberPdaV1(programId, pool, wallet);

  onDebugLog?.(`JoinPool V1 Program: ${programId.toBase58()}`);
  onDebugLog?.(`Wallet: ${wallet.toBase58()}`);
  onDebugLog?.(`Pool PDA: ${pool.toBase58()}`);
  onDebugLog?.(`Member PDA: ${member.toBase58()}`);

  // Idempotency: if member already exists, treat as already joined.
  const memberOwner = await getOwnerBase58(connection, member);
  if (isOwnedBy(memberOwner, programId)) {
    onDebugLog?.(`joinPoolV1: member already exists (owned by program). Skipping join.`);
    return "already-joined";
  }

  const ix = new TransactionInstruction({
    programId,
    keys: [
      { pubkey: wallet, isSigner: true, isWritable: true },
      { pubkey: pool, isSigner: false, isWritable: true },
      { pubkey: member, isSigner: false, isWritable: true },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
    ],
    data: ixData(DISC.join_pool),
  });

  return sendPhantomTx({
    connection,
    phantom,
    feePayer: wallet,
    ixs: [ix],
    onDebugLog,
  });
}

export async function contributeV1(args: {
  connection: Connection;
  wallet: PublicKey;
  phantom: PhantomProvider;
  programId: PublicKey;
  lamports: bigint;
  onDebugLog?: (line: string) => void;
}): Promise<string> {
  const { connection, wallet, phantom, programId, lamports, onDebugLog } = args;

  const [pool] = derivePoolPdaV1(programId, wallet);
  const [member] = deriveMemberPdaV1(programId, pool, wallet);
  const [vault] = deriveVaultPdaV1(programId, pool);

  onDebugLog?.(`Contribute V1 Program: ${programId.toBase58()}`);
  onDebugLog?.(`Wallet: ${wallet.toBase58()}`);
  onDebugLog?.(`Pool PDA: ${pool.toBase58()}`);
  onDebugLog?.(`Member PDA: ${member.toBase58()}`);
  onDebugLog?.(`Vault PDA: ${vault.toBase58()}`);
  onDebugLog?.(`Lamports: ${lamports.toString()}`);

  const ix = new TransactionInstruction({
    programId,
    keys: [
      { pubkey: wallet, isSigner: true, isWritable: true },
      { pubkey: pool, isSigner: false, isWritable: true },
      { pubkey: member, isSigner: false, isWritable: true },
      { pubkey: vault, isSigner: false, isWritable: true },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
    ],
    data: ixData(DISC.contribute, u64ToLeBytes(lamports)),
  });

  return sendPhantomTx({
    connection,
    phantom,
    feePayer: wallet,
    ixs: [ix],
    onDebugLog,
  });
}

// ========= V2
export async function createPoolV2(args: {
  connection: Connection;
  authority: PublicKey;
  phantom: PhantomProvider;
  programId: PublicKey;
  onDebugLog?: (line: string) => void;
}): Promise<string> {
  const { connection, authority, phantom, programId, onDebugLog } = args;

  const [poolV2] = derivePoolPdaV2(programId, authority);
  const [vaultV2] = deriveVaultPdaV2(programId, poolV2);

  onDebugLog?.(`CreatePool V2 Program: ${programId.toBase58()}`);
  onDebugLog?.(`Authority: ${authority.toBase58()}`);
  onDebugLog?.(`PoolV2 PDA: ${poolV2.toBase58()}`);
  onDebugLog?.(`VaultV2 PDA: ${vaultV2.toBase58()}`);

  // Best practice idempotency:
  // If pool already exists and is owned by the program, do not attempt to re-init.
  const poolOwner = await getOwnerBase58(connection, poolV2);
  if (isOwnedBy(poolOwner, programId)) {
    onDebugLog?.(`createPoolV2: pool already exists (owned by program). Skipping create.`);
    return "already-created";
  }

  const ix = new TransactionInstruction({
    programId,
    keys: [
      { pubkey: authority, isSigner: true, isWritable: true },
      { pubkey: poolV2, isSigner: false, isWritable: true },
      { pubkey: vaultV2, isSigner: false, isWritable: true },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
    ],
    data: ixData(DISC.create_pool_v2),
  });

  return sendPhantomTx({
    connection,
    phantom,
    feePayer: authority,
    ixs: [ix],
    onDebugLog,
  });
}

export async function joinPoolV2(args: {
  connection: Connection;
  wallet: PublicKey;
  phantom: PhantomProvider;
  programId: PublicKey;
  onDebugLog?: (line: string) => void;
}): Promise<string> {
  const { connection, wallet, phantom, programId, onDebugLog } = args;

  const [poolV2] = derivePoolPdaV2(programId, wallet);
  const [memberV2] = deriveMemberPdaV2(programId, poolV2, wallet);

  onDebugLog?.(`JoinPool V2 Program: ${programId.toBase58()}`);
  onDebugLog?.(`Wallet: ${wallet.toBase58()}`);
  onDebugLog?.(`PoolV2 PDA: ${poolV2.toBase58()}`);
  onDebugLog?.(`MemberV2 PDA: ${memberV2.toBase58()}`);

  // Best practice idempotency:
  // If member already exists and is owned by the program, do not attempt to re-init.
  const memberOwner = await getOwnerBase58(connection, memberV2);
  if (isOwnedBy(memberOwner, programId)) {
    onDebugLog?.(`joinPoolV2: member already exists (owned by program). Skipping join.`);
    return "already-joined";
  }

  const ix = new TransactionInstruction({
    programId,
    keys: [
      { pubkey: wallet, isSigner: true, isWritable: true },
      { pubkey: poolV2, isSigner: false, isWritable: true },
      { pubkey: memberV2, isSigner: false, isWritable: true },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
    ],
    data: ixData(DISC.join_pool_v2),
  });

  return sendPhantomTx({
    connection,
    phantom,
    feePayer: wallet,
    ixs: [ix],
    onDebugLog,
  });
}

export async function contributeV2(args: {
  connection: Connection;
  wallet: PublicKey;
  phantom: PhantomProvider;
  programId: PublicKey;
  lamports: bigint;
  onDebugLog?: (line: string) => void;
}): Promise<string> {
  const { connection, wallet, phantom, programId, lamports, onDebugLog } = args;

  const [poolV2] = derivePoolPdaV2(programId, wallet);
  const [memberV2] = deriveMemberPdaV2(programId, poolV2, wallet);
  const [vaultV2] = deriveVaultPdaV2(programId, poolV2);

  onDebugLog?.(`Contribute V2 Program: ${programId.toBase58()}`);
  onDebugLog?.(`Wallet: ${wallet.toBase58()}`);
  onDebugLog?.(`PoolV2 PDA: ${poolV2.toBase58()}`);
  onDebugLog?.(`MemberV2 PDA: ${memberV2.toBase58()}`);
  onDebugLog?.(`VaultV2 PDA: ${vaultV2.toBase58()}`);
  onDebugLog?.(`Lamports: ${lamports.toString()}`);

  const ix = new TransactionInstruction({
    programId,
    keys: [
      { pubkey: wallet, isSigner: true, isWritable: true },
      { pubkey: poolV2, isSigner: false, isWritable: true },
      { pubkey: memberV2, isSigner: false, isWritable: true },
      { pubkey: vaultV2, isSigner: false, isWritable: true },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
    ],
    data: ixData(DISC.contribute_v2, u64ToLeBytes(lamports)),
  });

  return sendPhantomTx({
    connection,
    phantom,
    feePayer: wallet,
    ixs: [ix],
    onDebugLog,
  });
}

// ===== Execute Payout (V2)
export async function executePayoutV2(args: {
  connection: Connection;
  signer: PublicKey; // authority or operator
  phantom: PhantomProvider;
  programId: PublicKey;
  recipient: PublicKey; // MUST match member_v2.wallet for current index
  onDebugLog?: (line: string) => void;
}): Promise<string> {
  const { connection, signer, phantom, programId, recipient, onDebugLog } = args;

  const [poolV2] = derivePoolPdaV2(programId, signer);
  const [vaultV2] = deriveVaultPdaV2(programId, poolV2);
  const [memberV2] = deriveMemberPdaV2(programId, poolV2, recipient);

  onDebugLog?.(`ExecutePayout V2 Program: ${programId.toBase58()}`);
  onDebugLog?.(`Signer: ${signer.toBase58()}`);
  onDebugLog?.(`PoolV2 PDA: ${poolV2.toBase58()}`);
  onDebugLog?.(`VaultV2 PDA: ${vaultV2.toBase58()}`);
  onDebugLog?.(`MemberV2 PDA: ${memberV2.toBase58()}`);
  onDebugLog?.(`Recipient: ${recipient.toBase58()}`);

  const ix = new TransactionInstruction({
    programId,
    keys: [
      { pubkey: signer, isSigner: true, isWritable: true },
      { pubkey: poolV2, isSigner: false, isWritable: true },
      { pubkey: vaultV2, isSigner: false, isWritable: true },
      { pubkey: memberV2, isSigner: false, isWritable: true },
      { pubkey: recipient, isSigner: false, isWritable: true },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
    ],
    data: ixData(DISC.execute_payout_v2),
  });

  return sendPhantomTx({
    connection,
    phantom,
    feePayer: signer,
    ixs: [ix],
    onDebugLog,
  });
}

// ===== Advance Round (V2)
export async function advanceRoundV2(args: {
  connection: Connection;
  signer: PublicKey; // authority or operator
  phantom: PhantomProvider;
  programId: PublicKey;
  onDebugLog?: (line: string) => void;
}): Promise<string> {
  const { connection, signer, phantom, programId, onDebugLog } = args;

  const [poolV2] = derivePoolPdaV2(programId, signer);

  onDebugLog?.(`AdvanceRound V2 Program: ${programId.toBase58()}`);
  onDebugLog?.(`Signer: ${signer.toBase58()}`);
  onDebugLog?.(`PoolV2 PDA: ${poolV2.toBase58()}`);

  const ix = new TransactionInstruction({
    programId,
    keys: [
      { pubkey: signer, isSigner: true, isWritable: true },
      { pubkey: poolV2, isSigner: false, isWritable: true },
    ],
    data: ixData(DISC.advance_round_v2),
  });

  return sendPhantomTx({
    connection,
    phantom,
    feePayer: signer,
    ixs: [ix],
    onDebugLog,
  });
}

// ===== Migration: V1 -> V2 (EXPORT REQUIRED by SuSuActionsPanel import)
export async function migrateToV2(args: {
  connection: Connection;
  authority: PublicKey;
  phantom: PhantomProvider;
  programId: PublicKey;
  setOperator: PublicKey; // pass PublicKey.default to signal "use authority" (program-side)
  deprecateV1: boolean;
  onDebugLog?: (line: string) => void;
}): Promise<string> {
  const { connection, authority, phantom, programId, setOperator, deprecateV1, onDebugLog } = args;

  const [poolV1] = derivePoolPdaV1(programId, authority);
  const [poolV2] = derivePoolPdaV2(programId, authority);
  const [vaultV2] = deriveVaultPdaV2(programId, poolV2);

  onDebugLog?.(`MigrateToV2 Program: ${programId.toBase58()}`);
  onDebugLog?.(`Authority: ${authority.toBase58()}`);
  onDebugLog?.(`PoolV1 PDA: ${poolV1.toBase58()}`);
  onDebugLog?.(`PoolV2 PDA: ${poolV2.toBase58()}`);
  onDebugLog?.(`VaultV2 PDA: ${vaultV2.toBase58()}`);
  onDebugLog?.(`set_operator: ${setOperator.toBase58()}`);
  onDebugLog?.(`deprecate_v1: ${deprecateV1}`);

  // Args layout (common Anchor pattern):
  // - set_operator: Pubkey (32 bytes)
  // - deprecate_v1: bool (u8)
  const argBytes = Buffer.concat([pubkeyToBytes(setOperator), boolToU8(deprecateV1)]);

  const ix = new TransactionInstruction({
    programId,
    keys: [
      { pubkey: authority, isSigner: true, isWritable: true },
      { pubkey: poolV1, isSigner: false, isWritable: true },
      { pubkey: poolV2, isSigner: false, isWritable: true },
      { pubkey: vaultV2, isSigner: false, isWritable: true },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
    ],
    data: ixData(DISC.migrate_to_v2, argBytes),
  });

  return sendPhantomTx({
    connection,
    phantom,
    feePayer: authority,
    ixs: [ix],
    onDebugLog,
  });
}
